import React, { useState } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "axios";
import { ServiceAPI } from "../../Api/ServiceAPI";
import "./Inventura.css";

const InventuraAdmin = () => {
  const [pastedJson, setPastedJson] = useState("");
  const [currentDate, setCurrentDate] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [availableLocations, setAvailableLocations] = useState([]);
  const [fishData, setFishData] = useState([]);
  const [rawData, setRawData] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const getCurrentTime = () => {
    const now = new Date();
    return `${now.getHours().toString().padStart(2, "0")}_${now
      .getMinutes()
      .toString()
      .padStart(2, "0")}_${now.getSeconds().toString().padStart(2, "0")}`;
  };

  const buildFishDataForLocation = (parsedData, date, location) => {
    if (!parsedData || !date || !location) return [];

    const locationData = parsedData?.[date]?.[location] || [];

    return locationData.map((item) => ({
      acIdent: item.acIdent || "",
      Fish_Name: item.Fish_Name || "",
      stanje: "",
      otpis: "",
    }));
  };

  const handleLoadJson = () => {
    try {
      const parsed = JSON.parse(pastedJson);
      const dateKey = Object.keys(parsed)[0];

      if (!dateKey) {
        throw new Error("Ne postoji datum u JSON-u");
      }

      const locations = Object.keys(parsed[dateKey] || {});

      setCurrentDate(dateKey);
      setRawData(parsed);
      setAvailableLocations(locations);

      if (locations.length > 0) {
        const firstLocation = locations[0];
        setSelectedLocation(firstLocation);

        const loadedFishData = buildFishDataForLocation(
          parsed,
          dateKey,
          firstLocation
        );
        setFishData(loadedFishData);
      } else {
        setSelectedLocation("");
        setFishData([]);
      }

      setErrors({});
    } catch (error) {
      alert("Neispravan JSON format ili struktura!");
      console.error(error);
    }
  };

  const handleLocationChange = (location) => {
    setSelectedLocation(location);

    const loadedFishData = buildFishDataForLocation(rawData, currentDate, location);
    setFishData(loadedFishData);
    setErrors({});
  };

  const handleInputChange = (index, field, value) => {
    const updatedFishData = [...fishData];
    const updatedErrors = { ...errors };

    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      updatedFishData[index][field] = value;
      delete updatedErrors[`${index}-${field}`];
    } else {
      updatedErrors[`${index}-${field}`] = "Neispravan unos (samo brojevi)";
    }

    setFishData(updatedFishData);
    setErrors(updatedErrors);
  };

  const validateData = () => {
    const newErrors = {};

    fishData.forEach((fish, index) => {
      if (!String(fish.stanje).trim()) {
        newErrors[`${index}-stanje`] =
          "Obavezno polje (unesite 0 ako nema stanja)";
      }
    });

    return newErrors;
  };

  const uploadToCloud = async (fileBlob, fileName) => {
    try {
      const token =
        localStorage.getItem("token") || sessionStorage.getItem("token");

      axios.defaults.headers.common["user_authorization"] = `Bearer ${token}`;

      const authResponse = await ServiceAPI.login();
      if (authResponse.data.result !== 0) {
        throw new Error("Authentication failed");
      }

      const folderId = 15546135840;

      await ServiceAPI.upload_file(
        authResponse.data.auth,
        folderId,
        fileBlob,
        () => {},
        0,
        undefined,
        fileName
      );

      console.log("Upload successful for", fileName);
      return true;
    } catch (error) {
      console.error("Upload error:", error);
      throw error;
    }
  };

  const generatePDF = (data) => {
    const doc = new jsPDF("p", "pt");
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor("#286496");
    doc.text("Fish Data Report", pageWidth / 2, 40, { align: "center" });

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(`LOCATION: ${selectedLocation.toUpperCase()}`, pageWidth / 2, 60, {
      align: "center",
    });

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.setTextColor("#000000");
    doc.text(
      `Date: ${currentDate} Time: ${getCurrentTime()}`,
      pageWidth / 2,
      75,
      {
        align: "center",
      }
    );

    const startY = 95;

    autoTable(doc, {
      startY,
      head: [["AC IDENT", "NAZIV RIBE", "STANJE (kg)", "OTPIS (kg)"]],
      body: data.map((item) => [
        item.acIdent,
        item.Fish_Name,
        item.stanje || "0",
        item.otpis || "0",
      ]),
      theme: "grid",
      headStyles: {
        fillColor: [40, 100, 150],
        textColor: [255, 255, 255],
        fontSize: 10,
        fontStyle: "bold",
        halign: "center",
      },
      bodyStyles: {
        fontSize: 10,
        textColor: [0, 0, 0],
        halign: "center",
      },
      styles: {
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
      },
      margin: { top: 10, bottom: 20, left: 40, right: 40 },
    });

    const pdfBlob = doc.output("blob");
    const fileName = `fish_data_report_${selectedLocation.toLowerCase()}_${currentDate}_time_${getCurrentTime()}.pdf`;

    return uploadToCloud(pdfBlob, fileName);
  };

  const generateCSV = (data) => {
    const csvContent = [
      `Location: ${selectedLocation}`,
      `Date: ${currentDate}`,
      "AC IDENT,NAZIV RIBE,STANJE (kg),OTPIS (kg)",
      ...data.map(
        (item) =>
          `${item.acIdent},"${item.Fish_Name}",${item.stanje || "0"},${
            item.otpis || "0"
          }`
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const fileName = `FishReport_${selectedLocation}_${currentDate.replace(
      /\./g,
      "-"
    )}.csv`;

    return uploadToCloud(blob, fileName);
  };

  const handleSave = async () => {
    const validationErrors = validateData();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      alert("Popravite greške prije spremanja!");
      return;
    }

    setSaving(true);

    try {
      await Promise.all([generatePDF(fishData), generateCSV(fishData)]);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 4000);
    } catch (error) {
      console.error("Greška pri spremanju:", error);
      alert("Spremanje nije uspjelo!");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="inventura-container">
      <div className="json-section">
        <h2>Učitaj JSON podatke</h2>
        <textarea
          className="json-input"
          value={pastedJson}
          onChange={(e) => setPastedJson(e.target.value)}
          placeholder="Zalijepi JSON ovdje..."
          rows={8}
        />
        <button
          className="load-button"
          onClick={handleLoadJson}
          disabled={!pastedJson.trim()}
        >
          Učitaj JSON
        </button>
      </div>

      {rawData && (
        <div className="location-controls">
          <div className="location-selector">
            <label>Odaberi lokaciju:</label>
            <select
              value={selectedLocation}
              onChange={(e) => handleLocationChange(e.target.value)}
              disabled={availableLocations.length === 0}
            >
              {availableLocations.map((loc, index) => (
                <option key={index} value={loc}>
                  {loc}
                </option>
              ))}
            </select>
          </div>

          <div className="date-info">Datum inventure: {currentDate}</div>
        </div>
      )}

      <div className="data-table-container">
        <table className="fish-data-table">
          <thead>
            <tr>
              <th>AC IDENT</th>
              <th>NAZIV RIBE</th>
              <th>STANJE (kg)</th>
              <th>OTPIS (kg)</th>
            </tr>
          </thead>
          <tbody>
            {fishData.map((item, index) => (
              <tr key={index}>
                <td>{item.acIdent}</td>
                <td>{item.Fish_Name}</td>
                <td>
                  <input
                    type="number"
                    step="0.01"
                    className={`data-input ${
                      errors[`${index}-stanje`] ? "input-error" : ""
                    }`}
                    value={item.stanje}
                    onChange={(e) =>
                      handleInputChange(index, "stanje", e.target.value)
                    }
                  />
                  {errors[`${index}-stanje`] && (
                    <div className="error-message">
                      {errors[`${index}-stanje`]}
                    </div>
                  )}
                </td>
                <td>
                  <input
                    type="number"
                    step="0.01"
                    className={`data-input ${
                      errors[`${index}-otpis`] ? "input-error" : ""
                    }`}
                    value={item.otpis}
                    onChange={(e) =>
                      handleInputChange(index, "otpis", e.target.value)
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {fishData.length === 0 && rawData && (
          <div className="no-data-message">
            Nema dostupnih podataka za odabranu lokaciju
          </div>
        )}
      </div>

      {rawData && (
        <div className="save-controls">
          <button
            className="save-button"
            onClick={handleSave}
            disabled={saving || fishData.length === 0}
          >
            {saving ? "Spremanje u tijeku..." : "Spremi izvještaj"}
          </button>

          {saveSuccess && (
            <div className="save-success">
              ✓ Podaci uspješno spremljeni u oblaku
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InventuraAdmin;